import * as React from "react";
import { Download, Upload } from "lucide-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { downloadAllFilesZip } from "@/ajax/documents/downloadAllFilesZip";
import Loading from "@/components/Loading";
import { Documents } from "@/components/Documents/Documents";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";

export const Route = createFileRoute("/_provider/provider/documents/")({
  validateSearch: z.object({
    fileId: z.string().optional(),
  }),
  component: DocumentsPage,
});

function DocumentsPage() {
  const trpc = useTRPC();
  const [showUploadSection, setShowUploadSection] = React.useState(false);

  const filesQueryState = useSuspenseQuery(
    trpc.credentialFiles.getFiles.queryOptions({
      filterId: undefined,
      switchUserId: undefined,
    }),
  );

  const providerUserQueryState = useSuspenseQuery(
    trpc.provider.getProviderUser.queryOptions(),
  );
  const providerUser = providerUserQueryState.data;

  const [downloadLoading, setDownloadLoading] = React.useState(false);

  const handleDownloadAll = async () => {
    setDownloadLoading(true);
    try {
      await downloadAllFilesZip({
        userId: providerUser.id,
        files: filesQueryState.data,
        userName: [providerUser.firstName, providerUser.lastName]
          .filter(Boolean)
          .join(" "),
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="w-full p-2 pt-4 md:pt-4 md:p-4 min-h-screen">
      <div className="max-w-4xl mx-auto flex justify-between items-center mb-8 px-4 md:px-0">
        <h2 className="text-title-md2 font-bold text-black dark:text-white">
          Documents
        </h2>
        <div className="flex gap-2 flex-col sm:flex-row">
          {!showUploadSection && (
            <Button
              variant="outline"
              onClick={() => setShowUploadSection(!showUploadSection)}
            >
              <Upload className="mr-2" />
              Upload Files
            </Button>
          )}
          <Button
            disabled={downloadLoading}
            variant="outline"
            onClick={handleDownloadAll}
          >
            {downloadLoading ? (
              <>
                <Loading className="w-6 h-6 mr-2" /> Generating zip...
              </>
            ) : (
              <>
                <Download className="mr-2" />
                Download All Files
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4 rounded-xl shadow-xs border bg-white animate-fade-in-delay-2 overflow-x-auto">
        <Documents
          showErrorOnMissingDocuments={true}
          providerUser={providerUser}
          showUpload={showUploadSection}
        />
      </div>
    </div>
  );
}
